// frontend/src/components/StockTransfer.jsx
import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Package, Warehouse, Plus, Building, Calendar, Hash, DollarSign,
  ClipboardList, Download, AlertCircle, Save, X, RefreshCw,
  ArrowLeftRight, AlertTriangle, ArrowUp, ArrowDown,
  Loader2, Truck, FileText, Percent, Trash2, Calculator,
  Layers, Box, User, MapPin, Clock, Check, Send, TrendingUp, TrendingDown
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import "./StockTransfer.css";

const url = process.env.REACT_APP_BACKEND_URL;

const StockTransfer = () => {
  const { currentUser, userType, company } = useAuth();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productStock, setProductStock] = useState({});
  const [transactionTypes, setTransactionTypes] = useState([]);

  const [loadingProducts, setLoadingProducts] = useState(false);
  const [sourceWarehouseStock, setSourceWarehouseStock] = useState({});

  const [formData, setFormData] = useState({
    company_id: "",
    transfer_date: new Date().toISOString().split('T')[0],
    notes: "",
  });

  const [transferItems, setTransferItems] = useState([
    {
      product_id: "",
      from_warehouse_id: "",
      to_warehouse_id: "",
      quantity: "",
      unit_cost: "",
      total_cost: "0",
      batch_number: "",
      expiry_date: "",
      description: "",
    }
  ]);

  const [errors, setErrors] = useState({});
  const [focusedFields, setFocusedFields] = useState({});

  const [totals, setTotals] = useState({
    totalItems: 0,
    totalQuantity: 0,
    totalValue: 0,
  });

  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  const isCompanyAdmin = userType === 'company' && (currentUser?.role === 'admin' || currentUser?.role === 'purchase' || currentUser?.role === 'user');
  const canCreate = isCompanyAdmin || isSystemAdmin;

  const getCompanyId = () => {
    if (userType === 'company' && company) {
      return company._id || company;
    }
    if (userType === 'system' && currentUser?.companyId) {
      return currentUser.companyId;
    }
    return null;
  };

  const fetchSourceWarehouseStock = async (warehouseId, productId) => {
    if (!warehouseId || !productId) return;

    try {
      const companyId = getCompanyId();

      const response = await axios.get(`${url}/api/inventorystock/warehouse/${warehouseId}`, {
        params: {
          company_id: companyId,
          product_id: productId,
          status: 'active'
        }
      });

      if (response.data.success) {
        const stockItem = response.data.data.find(
          item => item.product_id?._id === productId || item.product_id === productId
        );

        if (stockItem) {
          setSourceWarehouseStock(prev => ({
            ...prev,
            [warehouseId]: {
              [productId]: {
                current_stock: stockItem.quantity || 0,
                available_stock: stockItem.available_qty || 0,
                avg_cost: stockItem.avg_cost || 0
              }
            }
          }));

          if (stockItem.avg_cost && stockItem.avg_cost > 0) {
            const updated = [...transferItems];
            const index = updated.findIndex(item =>
              item.from_warehouse_id === warehouseId && item.product_id === productId
            );
            if (index !== -1) {
              updated[index].unit_cost = stockItem.avg_cost.toFixed(2);
              calculateItemTotal(index, updated[index].quantity, stockItem.avg_cost);
              setTransferItems(updated);
            }
          }
        }
      }
    } catch (error) {
      // Silent fail
    }
  };

  const fetchProductsForWarehouse = async (warehouseId) => {
    if (!warehouseId) {
      setFilteredProducts(products);
      setProductStock({});
      return;
    }

    try {
      setLoadingProducts(true);
      const companyId = getCompanyId();

      const response = await axios.get(`${url}/api/inventorystock/warehouse/${warehouseId}`, {
        params: {
          company_id: companyId,
          status: 'active'
        }
      });

      if (response.data.success) {
        const stockMap = {};
        const warehouseProducts = (response.data.data || []).map(stock => {
          const product = stock.product_id || {};
          stockMap[product._id] = {
            current_stock: stock.quantity || 0,
            available_stock: stock.available_qty || 0,
            avg_cost: stock.avg_cost || 0
          };
          return {
            _id: product._id,
            product_id: product._id,
            product_code: product.product_code || 'N/A',
            product_name: product.product_name || 'Unknown',
            current_stock: stock.quantity || 0,
            available_stock: stock.available_qty || 0,
            avg_cost: stock.avg_cost || 0
          };
        });

        setProductStock(stockMap);
        setFilteredProducts(warehouseProducts);
      } else {
        setFilteredProducts(products);
        setProductStock({});
      }
    } catch (error) {
      setFilteredProducts(products);
      setProductStock({});
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleFromWarehouseChange = (index, warehouseId) => {
    const updated = [...transferItems];
    updated[index].from_warehouse_id = warehouseId;
    updated[index].product_id = "";
    updated[index].quantity = "";
    updated[index].unit_cost = "";
    setTransferItems(updated);

    if (warehouseId) {
      fetchProductsForWarehouse(warehouseId);
    }
  };

  const handleProductChange = (index, productId) => {
    const updated = [...transferItems];
    updated[index].product_id = productId;
    setTransferItems(updated);

    const warehouseId = updated[index].from_warehouse_id;
    if (warehouseId && productId) {
      fetchSourceWarehouseStock(warehouseId, productId);
    }
  };

  const calculateItemTotal = (index, quantity, unitCost) => {
    const updated = [...transferItems];
    const qty = parseFloat(quantity) || 0;
    const cost = parseFloat(unitCost) || 0;
    const total = (qty * cost).toFixed(2);
    updated[index].total_cost = total;
    setTransferItems(updated);
  };

  useEffect(() => {
    let totalQty = 0;
    let totalVal = 0;
    let itemCount = 0;

    transferItems.forEach(item => {
      const qty = parseFloat(item.quantity) || 0;
      const val = parseFloat(item.total_cost) || 0;

      if (item.product_id && qty > 0) {
        totalQty += qty;
        totalVal += val;
        itemCount++;
      }
    });

    setTotals({
      totalItems: itemCount,
      totalQuantity: totalQty,
      totalValue: totalVal,
    });
  }, [transferItems]);

  useEffect(() => {
    const fetchData = async () => {
      if (!canCreate) {
        toast.error("You don't have permission");
        navigate("/");
        return;
      }

      try {
        setLoading(true);

        const companyId = getCompanyId();

        if (!companyId) {
          setError("No company ID found");
          setLoading(false);
          return;
        }

        const params = { company_id: companyId, status: 'active' };

        try {
          const whRes = await axios.get(`${url}/api/warehouse/dropdown`, { params });
          if (whRes.data?.success) {
            setWarehouses(whRes.data.data || []);
          }
        } catch (err) {
          toast.error("Failed to load warehouses");
        }

        try {
          const prodRes = await axios.get(`${url}/api/products/dropdown`, { params });
          if (prodRes.data?.success) {
            setProducts(prodRes.data.data || []);
          }
        } catch (err) {
          toast.error("Failed to load products");
        }

        try {
          const ttRes = await axios.get(`${url}/api/transactiontype/dropdown`, { params });
          if (ttRes.data?.success) {
            const allTypes = ttRes.data.data || [];

            const transferTypes = allTypes.filter(type => {
              const typeName = (type.transaction_type || '').toLowerCase();
              return typeName === 'transfer_in' ||
                typeName === 'transfer_out' ||
                typeName.includes('transfer');
            });

            setTransactionTypes(transferTypes);
          }
        } catch (err) {
          // silent
        }

        setFormData(prev => ({ ...prev, company_id: companyId }));

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleFocus = (field) => setFocusedFields(prev => ({ ...prev, [field]: true }));
  const handleBlur = (field) => {
    const val = formData[field];
    setFocusedFields(prev => ({ ...prev, [field]: !!val }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...transferItems];

    if (field === 'from_warehouse_id') {
      handleFromWarehouseChange(index, value);
    } else if (field === 'product_id') {
      handleProductChange(index, value);
    } else if (field === 'quantity') {
      if (value === '' || /^\d+$/.test(value)) {
        updated[index][field] = value;
        calculateItemTotal(index, value, updated[index].unit_cost);
        setTransferItems(updated);
      }
    } else if (field === 'unit_cost') {
      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
        updated[index][field] = value;
        calculateItemTotal(index, updated[index].quantity, value);
        setTransferItems(updated);
      }
    } else {
      updated[index][field] = value;
      setTransferItems(updated);
    }
  };

  const handleUnitCostBlur = (index) => {
    const updated = [...transferItems];
    const value = updated[index].unit_cost;
    if (value && value !== '') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        updated[index].unit_cost = numValue.toFixed(2);
        calculateItemTotal(index, updated[index].quantity, numValue);
        setTransferItems(updated);
      }
    }
  };

  const addItemRow = () => {
    setTransferItems([...transferItems, {
      product_id: "",
      from_warehouse_id: "",
      to_warehouse_id: "",
      quantity: "",
      unit_cost: "",
      total_cost: "0",
      batch_number: "",
      expiry_date: "",
      description: "",
    }]);
  };

  const removeItemRow = (index) => {
    if (transferItems.length > 1) {
      setTransferItems(transferItems.filter((_, i) => i !== index));
    } else {
      toast.warning("At least one transfer item is required");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.transfer_date) {
      newErrors.transfer_date = "Transfer date is required";
    }

    const validItems = transferItems.filter(item =>
      item.product_id &&
      item.from_warehouse_id &&
      item.to_warehouse_id &&
      item.quantity &&
      parseFloat(item.quantity) > 0
    );

    if (validItems.length === 0) {
      newErrors.items = "At least one valid transfer item is required";
    } else {
      transferItems.forEach((item, index) => {
        if (item.from_warehouse_id && item.to_warehouse_id &&
          item.from_warehouse_id === item.to_warehouse_id) {
          newErrors[`warehouse_${index}`] = "Source and destination warehouses must be different";
        }

        if (item.product_id && item.from_warehouse_id) {
          const qty = parseFloat(item.quantity) || 0;
          const stockInfo = sourceWarehouseStock[item.from_warehouse_id]?.[item.product_id];

          if (stockInfo && stockInfo.current_stock < qty) {
            newErrors[`stock_${index}`] = `Insufficient stock. Available: ${stockInfo.current_stock}`;
          }
        }

        if (item.product_id && (!item.quantity || parseFloat(item.quantity) <= 0)) {
          newErrors[`quantity_${index}`] = "Quantity must be greater than 0";
        }

        if (item.unit_cost && item.unit_cost.includes('.') && item.unit_cost.split('.')[1].length > 2) {
          newErrors[`unit_cost_${index}`] = "Unit cost cannot have more than 2 decimal places";
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCancel = () => {
    navigate("/ViewStockTransfer");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix form errors");
      return;
    }

    const confirm = window.confirm(
      `Create transfer for ${totals.totalQuantity} units across ${totals.totalItems} item(s)?`
    );
    if (!confirm) return;

    setSubmitting(true);

    try {
      const companyId = getCompanyId();

      if (!companyId) {
        toast.error("Company information is missing. Please reload the page.");
        return;
      }

      // Build the valid items payload.
      const validItems = transferItems
        .filter(item =>
          item.product_id &&
          item.from_warehouse_id &&
          item.to_warehouse_id &&
          item.quantity &&
          parseFloat(item.quantity) > 0
        )
        .map(item => ({
          product_id: item.product_id,
          from_warehouse_id: item.from_warehouse_id,
          to_warehouse_id: item.to_warehouse_id,
          quantity: parseInt(item.quantity, 10),
          unit_cost: parseFloat(parseFloat(item.unit_cost || 0).toFixed(2)),
          batch_number: item.batch_number || "",
          expiry_date: item.expiry_date || null,
          description: item.description || formData.notes || "Warehouse stock transfer",
          company_id: companyId,
          transfer_date: formData.transfer_date,
          notes: formData.notes || "",
        }));

      const payload = {
        transfers: validItems,
        transfer_date: formData.transfer_date,
        notes: formData.notes,
        company_id: companyId,
      };

      const res = await axios.post(`${url}/api/stocktransfers`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (res.data.success) {
        const refNo =
          res.data?.data?.transfers?.[0]?.reference_no || "N/A";
        toast.success(`Stock transfer created successfully! Reference: ${refNo}`);
        setTimeout(() => {
          navigate("/ViewStockTransfer");
        }, 1500);
      }
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;
      let userMessage = "Failed to create stock transfer";

      if (status === 400) {
        userMessage = data?.message || "Invalid transfer data. Please check the form.";
      } else if (status === 401 || status === 403) {
        userMessage = "Your login session is missing or expired. Please log in again.";
        setTimeout(() => navigate("/login"), 1500);
      } else if (status === 409) {
        userMessage = data?.message || "Duplicate reference number. Please try again.";
      } else if (status === 500) {
        userMessage =
          data?.message ||
          "Server error while creating the transfer. Please check the backend logs.";
      } else if (data?.errors) {
        userMessage =
          typeof data.errors === "object"
            ? Object.values(data.errors).filter(Boolean).join(" | ")
            : userMessage;
      } else if (data?.message) {
        userMessage = data.message;
      }

      toast.error(userMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amt) => {
    const abs = Math.abs(amt || 0);
    return '$' + abs.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
  };

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="transfer-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading stock transfer form data...</p>
            <p style={{ fontSize: '12px', color: '#999' }}>Please wait...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="form-wrapper">
        <div className="transfer-form-container">
          <div className="error-container">
            <AlertCircle size={48} color="#dc3545" />
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <button onClick={handleCancel} className="cancel-btn">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="transfer-form-container">
        <button className="close-btn" onClick={handleCancel} type="button">
          <X size={20} />
        </button>

        <div className="form-header">
          <ArrowLeftRight size={24} />
          <h2>Create Warehouse Stock Transfer</h2>
        </div>

        {transactionTypes.length > 0 && (
          <div style={{
            background: '#f0f5ff',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            border: '1px solid #d6e4ff'
          }}>
            <ArrowLeftRight size={18} color="#2f54eb" />
            <div style={{ fontSize: '13px' }}>
              <span style={{ fontWeight: 600, color: '#2f54eb' }}>Transaction Types:</span>{' '}
              {transactionTypes.map((type, idx) => (
                <span key={type._id}>
                  {idx > 0 && ' | '}
                  <span style={{
                    background: type.sign === '+' ? '#f6ffed' : '#fff2f0',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    color: type.sign === '+' ? '#52c41a' : '#ff4d4f',
                    margin: '0 4px'
                  }}>
                    {type.transaction_type} ({type.sign})
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Transfer Date *</label>
              <input
                type="date"
                name="transfer_date"
                value={formData.transfer_date}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <h3 className="section-title">
            <Package size={18} /> Transfer Items
          </h3>

          {errors.items && (
            <div className="error-message">
              <AlertCircle size={16} />
              <span>{errors.items}</span>
            </div>
          )}

          {transferItems.map((item, index) => (
            <div key={index} className="transfer-item-card">
              <div className="transfer-item-grid">
                <div className="form-group">
                  <label>From Warehouse *</label>
                  <select
                    value={item.from_warehouse_id}
                    onChange={(e) => handleItemChange(index, 'from_warehouse_id', e.target.value)}
                    required
                  >
                    <option value="">Select Source</option>
                    {warehouses.map(w => (
                      <option key={w._id} value={w._id}>
                        {w.warehouse_name} ({w.warehouse_code})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>To Warehouse *</label>
                  <select
                    value={item.to_warehouse_id}
                    onChange={(e) => handleItemChange(index, 'to_warehouse_id', e.target.value)}
                    required
                  >
                    <option value="">Select Destination</option>
                    {warehouses.map(w => (
                      <option key={w._id} value={w._id}>
                        {w.warehouse_name} ({w.warehouse_code})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Product *</label>
                  <Select
                    value={
                      item.product_id
                        ? (() => {
                          const matched = filteredProducts.find(
                            (p) => String(p._id) === String(item.product_id)
                          );
                          if (!matched) return null;
                          return {
                            value: matched._id,
                            label: `${matched.product_name} (${matched.product_code})${productStock[matched._id]
                                ? ` - Stock: ${productStock[matched._id].current_stock}`
                                : ""
                              }`,
                          };
                        })()
                        : null
                    }

                    options={filteredProducts.map((p) => ({
                      value: p._id,
                      label: `${p.product_name} (${p.product_code})${productStock[p._id]
                          ? ` - Stock: ${productStock[p._id].current_stock}`
                          : ""
                        }`,
                    }))}

                    onChange={(selected) =>
                      handleItemChange(index, "product_id", selected ? selected.value : "")
                    }

                    placeholder={
                      !item.from_warehouse_id
                        ? "Select source warehouse first"
                        : loadingProducts
                          ? "Loading products..."
                          : filteredProducts.length === 0
                            ? "No products available"
                            : "Search Product..."
                    }

                    isDisabled={!item.from_warehouse_id || loadingProducts}

                    isClearable

                    isSearchable

                    noOptionsMessage={() =>
                      !item.from_warehouse_id
                        ? "Select source warehouse first"
                        : loadingProducts
                          ? "Loading..."
                          : "No products available"
                    }

                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "38px",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      menuList: (base) => ({
                        ...base,
                        maxHeight: 250,
                      }),
                    }}
                  />
                </div>

                <div className="form-group">
                  <label>Quantity *</label>
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    placeholder="0"
                    min="1"
                    step="1"
                    required
                    onKeyDown={(e) => {
                      if (e.key === '.') {
                        e.preventDefault();
                      }
                    }}
                  />
                  <small style={{ color: '#999', fontSize: '10px' }}>Whole numbers only</small>
                </div>

                <div className="form-group">
                  <label>Unit Cost</label>
                  <input
                    type="number"
                    value={item.unit_cost}
                    onChange={(e) => handleItemChange(index, 'unit_cost', e.target.value)}
                    onBlur={() => handleUnitCostBlur(index)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                  <small style={{ color: '#999', fontSize: '10px' }}>Max 2 decimal places</small>
                </div>

                <div className="form-group">
                  <label>Total</label>
                  <input
                    type="text"
                    value={formatCurrency(parseFloat(item.total_cost) || 0)}
                    readOnly
                    className="total-field"
                  />
                </div>
              </div>

              {item.from_warehouse_id && item.product_id &&
                sourceWarehouseStock[item.from_warehouse_id]?.[item.product_id] && (
                  <div className={`stock-info ${sourceWarehouseStock[item.from_warehouse_id][item.product_id].current_stock < (parseFloat(item.quantity) || 0)
                      ? 'insufficient'
                      : 'sufficient'
                    }`}>
                    <Layers size={14} />
                    <span>
                      Available in source: <strong>
                        {sourceWarehouseStock[item.from_warehouse_id][item.product_id].current_stock}
                      </strong> units
                      {sourceWarehouseStock[item.from_warehouse_id][item.product_id].current_stock < (parseFloat(item.quantity) || 0) && (
                        <span className="warning"> ⚠️ Insufficient stock!</span>
                      )}
                    </span>
                  </div>
                )}

              {errors[`warehouse_${index}`] && (
                <div className="error-message small">
                  {errors[`warehouse_${index}`]}
                </div>
              )}

              {errors[`unit_cost_${index}`] && (
                <div className="error-message small">
                  {errors[`unit_cost_${index}`]}
                </div>
              )}

              <div className="batch-expiry-row">
                <input
                  type="text"
                  placeholder="Batch Number (optional)"
                  value={item.batch_number}
                  onChange={(e) => handleItemChange(index, 'batch_number', e.target.value)}
                />
                <input
                  type="date"
                  placeholder="Expiry Date"
                  value={item.expiry_date}
                  onChange={(e) => handleItemChange(index, 'expiry_date', e.target.value)}
                />
              </div>

              <input
                type="text"
                placeholder="Item specific notes (optional)"
                value={item.description}
                onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                className="item-notes"
              />

              {index > 0 && (
                <button
                  type="button"
                  onClick={() => removeItemRow(index)}
                  className="remove-item-btn"
                >
                  <Trash2 size={14} /> Remove
                </button>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={addItemRow}
            className="add-item-btn"
          >
            <Plus size={16} /> Add Another Item
          </button>

          <div className="form-group">
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Additional notes for this transfer..."
              rows="3"
            />
          </div>

          <div className="totals-summary">
            <h4>Transfer Summary</h4>
            <div className="totals-grid">
              <div className="total-item">
                <div className="total-label">Total Items</div>
                <div className="total-value">{totals.totalItems}</div>
              </div>
              <div className="total-item">
                <div className="total-label">Total Quantity</div>
                <div className="total-value highlight">{totals.totalQuantity}</div>
              </div>
              <div className="total-item">
                <div className="total-label">Total Value</div>
                <div className="total-value highlight">{formatCurrency(totals.totalValue)}</div>
              </div>
            </div>
          </div>

          <div className="action-buttons">
            <button
              type="button"
              onClick={handleCancel}
              disabled={submitting}
              className="cancel-btn"
            >
              <X size={16} /> Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Creating Transfer...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Create Transfer
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StockTransfer;
